import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { ScrollArea } from "../ui/scroll-area";
import {
  Plus, Trash2, Search, Package, Edit2, Save, X, Check, Tag, BookmarkPlus, Upload, Image as ImageIcon, Settings2
} from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";
import { itemPhotoSrc } from "./InvoiceFormDialog";
import ItemsImportModal from "./ItemsImportModal";
import CategoryTagManager from "./CategoryTagManager";

// ─── Items Library Tab ──────────────────────────────────────────
export const ItemsLibrary = ({ isDark, headers }) => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", unit_price: 0, tax_rate: 0, category: "", default_quantity: 1, sku: "", photo_url: "", tags: [] });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => { fetchItems(); fetchTaxonomy(); }, []);

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/invoices/library/items`, { headers });
      setItems(res.data || []);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  const fetchTaxonomy = async () => {
    try {
      const [cats, tgs] = await Promise.all([
        axios.get(`${API}/invoices/library/categories`, { headers }),
        axios.get(`${API}/invoices/library/tags`, { headers }),
      ]);
      setCategories(cats.data || []);
      setTags(tgs.data || []);
    } catch { /* silent */ }
  };

  const resetForm = () => setForm({ name: "", description: "", unit_price: 0, tax_rate: 0, category: "", default_quantity: 1, sku: "", photo_url: "", tags: [] });

  const toggleTag = (name) =>
    setForm(f => ({ ...f, tags: f.tags.includes(name) ? f.tags.filter(t => t !== name) : [...f.tags, name] }));

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/invoices/item-photos`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setForm(f => ({ ...f, photo_url: res.data.photo_url }));
      toast.success("Item photo added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Photo upload failed");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const saveItem = async () => {
    if (!form.name.trim()) { toast.error("Item name is required"); return; }
    try {
      if (editingId) {
        await axios.put(`${API}/invoices/library/items/${editingId}`, form, { headers });
        toast.success("Item updated");
      } else {
        await axios.post(`${API}/invoices/library/items`, form, { headers });
        toast.success("Item saved to library");
      }
      setEditingId(null);
      setShowAdd(false);
      resetForm();
      fetchItems();
    } catch { toast.error("Failed to save item"); }
  };

  const deleteItem = async (id) => {
    try {
      await axios.delete(`${API}/invoices/library/items/${id}`, { headers });
      toast.success("Item deleted");
      setItems(prev => prev.filter(i => i.item_id !== id));
    } catch { toast.error("Failed to delete item"); }
  };

  const startEdit = (item) => {
    setEditingId(item.item_id);
    setForm({ name: item.name, description: item.description || "", unit_price: item.unit_price || 0, tax_rate: item.tax_rate || 0, category: item.category || "", default_quantity: item.default_quantity || 1, sku: item.sku || "", photo_url: item.photo_url || "", tags: item.tags || [] });
    setShowAdd(true);
  };

  const filtered = items.filter(i =>
    i.name.toLowerCase().includes(search.toLowerCase()) ||
    (i.description || "").toLowerCase().includes(search.toLowerCase()) ||
    (i.sku || "").toLowerCase().includes(search.toLowerCase()) ||
    (i.category || "").toLowerCase().includes(search.toLowerCase()) ||
    (i.tags || []).some(t => t.toLowerCase().includes(search.toLowerCase()))
  );

  const grouped = {};
  filtered.forEach(i => {
    const cat = i.category || "Uncategorized";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(i);
  });
  // Order groups by the managed category order; unknown names after, Uncategorized last
  const catOrder = Object.fromEntries(categories.map((c, i) => [c.name, i]));
  const orderedGroups = Object.entries(grouped).sort(([a], [b]) => {
    if (a === "Uncategorized") return 1;
    if (b === "Uncategorized") return -1;
    const ia = catOrder[a] ?? 999, ib = catOrder[b] ?? 999;
    return ia !== ib ? ia - ib : a.localeCompare(b);
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-3 justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" placeholder="Search items..." data-testid="library-search" />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowManage(true)}
            className={isDark ? "border-white/15 text-foreground hover:bg-white/[0.03]" : "border-gray-300 text-gray-700 hover:bg-gray-50"}
            data-testid="library-manage-btn"
          >
            <Settings2 className="w-4 h-4 mr-1.5" /> Categories & Tags
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowImport(true)}
            className={isDark ? "border-white/15 text-foreground hover:bg-white/[0.03]" : "border-gray-300 text-gray-700 hover:bg-gray-50"}
            data-testid="library-import-btn"
          >
            <Upload className="w-4 h-4 mr-1.5" /> Import
          </Button>
          <Button onClick={() => { resetForm(); setEditingId(null); setShowAdd(true); }} className={isDark ? "bg-[#FFCC00] text-black hover:bg-[#FFCC00]/90" : "bg-amber-500 text-white hover:bg-amber-600"} data-testid="library-add-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add Item
          </Button>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showAdd && (
        <div className={`p-4 rounded-xl border space-y-3 ${isDark ? "bg-white/[0.02] border-white/10" : "bg-gray-50 border-gray-200"}`} data-testid="library-form">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">{editingId ? "Edit Item" : "New Item"}</h3>
            <Button variant="ghost" size="icon" onClick={() => { setShowAdd(false); setEditingId(null); resetForm(); }} className="h-7 w-7"><X className="w-4 h-4" /></Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Name *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-8 text-sm mt-1" placeholder="e.g., AC Repair Service" data-testid="lib-name" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">SKU / Part #</Label>
              <Input value={form.sku} onChange={e => setForm(f => ({ ...f, sku: e.target.value }))} className="h-8 text-sm mt-1" placeholder="e.g., SVC-001" data-testid="lib-sku" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Description</Label>
            <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="h-8 text-sm mt-1" placeholder="Optional description..." data-testid="lib-desc" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Unit Price ($)</Label>
              <Input type="number" step="0.01" min="0" value={form.unit_price} onChange={e => setForm(f => ({ ...f, unit_price: parseFloat(e.target.value) || 0 }))} className="h-8 text-sm mt-1" data-testid="lib-price" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Tax Rate (%)</Label>
              <Input type="number" step="0.1" min="0" max="100" value={form.tax_rate} onChange={e => setForm(f => ({ ...f, tax_rate: parseFloat(e.target.value) || 0 }))} className="h-8 text-sm mt-1" data-testid="lib-tax" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Default Qty</Label>
              <Input type="number" min="1" value={form.default_quantity} onChange={e => setForm(f => ({ ...f, default_quantity: parseInt(e.target.value) || 1 }))} className="h-8 text-sm mt-1" data-testid="lib-qty" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Category</Label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className={`h-8 text-sm mt-1 w-full rounded-md border px-2 ${isDark ? "bg-transparent border-white/10 text-foreground" : "bg-white border-gray-200 text-gray-900"}`} data-testid="lib-category">
                <option value="">None</option>
                {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                {form.category && !categories.some(c => c.name === form.category) && (
                  <option value={form.category}>{form.category}</option>
                )}
              </select>
            </div>
          </div>
          {/* Tags — managed via Categories & Tags */}
          <div>
            <Label className="text-xs text-muted-foreground">Tags</Label>
            <div className="flex flex-wrap items-center gap-1.5 mt-1" data-testid="lib-tags">
              {tags.length === 0 ? (
                <button type="button" onClick={() => setShowManage(true)} className="text-[11px] text-muted-foreground underline hover:text-foreground" data-testid="lib-tags-empty">
                  No tags yet — create some in "Categories &amp; Tags"
                </button>
              ) : tags.map(t => {
                const on = form.tags.includes(t.name);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => toggleTag(t.name)}
                    className={`px-2 py-0.5 rounded-full text-[11px] border transition-colors ${
                      on
                        ? "bg-[#FFCC00]/15 text-[#b8930a] dark:text-[#FFCC00] border-[#FFCC00]/50 font-semibold"
                        : isDark ? "border-white/15 text-white/60 hover:border-white/30" : "border-gray-300 text-gray-500 hover:border-gray-400"
                    }`}
                    data-testid={`lib-tag-${t.id}`}
                  >
                    {t.name}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Item photo — shows on invoice/estimate PDFs when this item is used */}
          <div>
            <Label className="text-xs text-muted-foreground">Item Photo</Label>
            <div className="flex items-center gap-3 mt-1">
              {form.photo_url ? (
                <div className="relative inline-block" data-testid="lib-photo-wrap">
                  <img
                    src={itemPhotoSrc(form.photo_url)}
                    alt="Item"
                    className="w-14 h-14 rounded-lg object-cover border border-border"
                    data-testid="lib-photo-thumb"
                  />
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, photo_url: "" }))}
                    className="absolute -top-1.5 -right-1.5 w-4.5 h-4.5 rounded-full bg-red-500 text-white flex items-center justify-center"
                    data-testid="lib-photo-remove"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <label className={`flex items-center gap-1.5 px-3 h-8 rounded-lg border cursor-pointer text-xs ${isDark ? "border-white/15 text-white/70 hover:bg-white/[0.04]" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`} data-testid="lib-photo-label">
                  <ImageIcon className="w-3.5 h-3.5" />
                  {uploadingPhoto ? "Uploading…" : "Add photo"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploadingPhoto}
                    onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value = ""; }}
                    data-testid="lib-photo-input"
                  />
                </label>
              )}
              <p className="text-[10px] text-muted-foreground">Shown on invoice & estimate PDFs when this item is added</p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setShowAdd(false); setEditingId(null); resetForm(); }}>Cancel</Button>
            <Button size="sm" onClick={saveItem} className={isDark ? "bg-[#FFCC00] text-black hover:bg-[#FFCC00]/90" : "bg-amber-500 text-white hover:bg-amber-600"} data-testid="lib-save-btn">
              <Save className="w-3 h-3 mr-1" /> {editingId ? "Update" : "Save"}
            </Button>
          </div>
        </div>
      )}

      {/* Items List */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">Loading items...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16">
          <Package className={`w-12 h-12 mx-auto mb-3 ${isDark ? "text-white/10" : "text-gray-200"}`} />
          <p className="text-sm text-muted-foreground mb-1">No saved items yet</p>
          <p className="text-xs text-muted-foreground">Save frequently used services, parts, and materials for quick invoicing</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">No items match "{search}"</div>
      ) : (
        <div className="space-y-4">
          {orderedGroups.map(([cat, catItems]) => (
            <div key={cat}>
              <div className="flex items-center gap-2 mb-2">
                <Tag className="w-3 h-3 text-muted-foreground" />
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{cat}</span>
                <span className="text-[10px] text-muted-foreground">({catItems.length})</span>
              </div>
              <div className="space-y-1.5">
                {catItems.map(item => (
                  <div key={item.item_id} className={`flex items-center justify-between p-3 rounded-xl border transition-all group ${isDark ? "bg-white/[0.02] border-white/5 hover:border-white/10" : "bg-white border-gray-100 hover:border-gray-200 shadow-sm"}`} data-testid={`library-item-${item.item_id}`}>
                    <div className="flex-1 min-w-0 flex items-center gap-2.5">
                      {item.photo_url && (
                        <img
                          src={itemPhotoSrc(item.photo_url)}
                          alt=""
                          className="w-9 h-9 rounded-lg object-cover border border-border shrink-0"
                          data-testid={`library-item-photo-${item.item_id}`}
                        />
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">{item.name}</span>
                          {item.sku && <Badge variant="outline" className="text-[9px] h-4 px-1">{item.sku}</Badge>}
                          {(item.tags || []).map(t => (
                            <Badge key={t} className="text-[9px] h-4 px-1.5 bg-[#FFCC00]/15 text-[#b8930a] dark:text-[#FFCC00] border border-[#FFCC00]/30" data-testid={`item-tag-badge-${item.item_id}-${t}`}>{t}</Badge>
                          ))}
                        </div>
                        {item.description && <p className="text-xs text-muted-foreground truncate mt-0.5">{item.description}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 ml-3">
                      <span className={`text-sm font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>${(item.unit_price || 0).toFixed(2)}</span>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button variant="ghost" size="icon" onClick={() => startEdit(item)} className="h-7 w-7 text-muted-foreground hover:text-foreground"><Edit2 className="w-3 h-3" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => deleteItem(item.item_id)} className="h-7 w-7 text-red-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <ItemsImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        headers={headers}
        onImported={fetchItems}
        isDark={isDark}
      />
      <CategoryTagManager
        open={showManage}
        onOpenChange={setShowManage}
        categories={categories}
        tags={tags}
        onChanged={async () => { await fetchTaxonomy(); await fetchItems(); }}
        headers={headers}
        isDark={isDark}
      />
    </div>
  );
};


// ─── Item Picker Modal (Browse Items) ───────────────────────────
export const ItemPickerModal = ({ open, onOpenChange, onSelect, isDark, headers }) => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (open) {
      setSelected([]);
      setSearch("");
      axios.get(`${API}/invoices/library/items`, { headers })
        .then(res => setItems(res.data || []))
        .catch(() => setItems([]));
    }
  }, [open]);

  const filtered = items.filter(i =>
    i.name.toLowerCase().includes(search.toLowerCase()) ||
    (i.description || "").toLowerCase().includes(search.toLowerCase())
  );

  const toggleItem = (item) => {
    setSelected(prev => prev.find(s => s.item_id === item.item_id)
      ? prev.filter(s => s.item_id !== item.item_id)
      : [...prev, item]
    );
  };

  const confirm = () => {
    onSelect(selected.map(s => ({
      description: s.name + (s.description ? ` - ${s.description}` : ""),
      quantity: s.default_quantity || 1,
      unit_price: s.unit_price || 0,
      discount: 0,
      photo_url: s.photo_url || "",
    })));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`sm:max-w-md max-w-[95vw] p-0 overflow-hidden border-0 ${isDark ? "bg-[#111113]" : "bg-white"}`} data-testid="item-picker-modal">
        <DialogHeader className={`px-5 pt-5 pb-3 border-b ${isDark ? "border-white/5" : "border-gray-100"}`}>
          <DialogTitle className="text-base font-bold flex items-center gap-2">
            <Package className="w-5 h-5 text-[#FFCC00]" /> Browse Items
          </DialogTitle>
        </DialogHeader>
        <div className="px-5 pt-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" placeholder="Search items..." data-testid="picker-search" />
          </div>
        </div>
        <ScrollArea className="max-h-[350px] px-5 py-3">
          {filtered.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8">No items found</p>
          ) : (
            <div className="space-y-1.5">
              {filtered.map(item => {
                const isSelected = selected.some(s => s.item_id === item.item_id);
                return (
                  <button key={item.item_id} onClick={() => toggleItem(item)}
                    className={`w-full flex items-center justify-between p-3 rounded-xl text-left transition-all border ${
                      isSelected
                        ? isDark ? "bg-[#FFCC00]/10 border-[#FFCC00]/40" : "bg-amber-50 border-amber-300"
                        : isDark ? "bg-white/[0.02] border-white/5 hover:border-white/10" : "bg-white border-gray-100 hover:border-gray-200"
                    }`}
                    data-testid={`picker-item-${item.item_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">{item.name}</span>
                        {item.category && <Badge variant="outline" className="text-[9px] h-4 px-1">{item.category}</Badge>}
                      </div>
                      {item.description && <p className="text-xs text-muted-foreground truncate">{item.description}</p>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      <span className={`text-sm font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>${(item.unit_price || 0).toFixed(2)}</span>
                      {isSelected && <Check className="w-4 h-4 text-[#FFCC00]" />}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
        <div className={`flex items-center justify-between px-5 py-3 border-t ${isDark ? "border-white/5" : "border-gray-100"}`}>
          <span className="text-xs text-muted-foreground">{selected.length} item{selected.length !== 1 ? "s" : ""} selected</span>
          <Button onClick={confirm} disabled={selected.length === 0} className={isDark ? "bg-[#FFCC00] text-black hover:bg-[#FFCC00]/90" : "bg-amber-500 text-white hover:bg-amber-600"} data-testid="picker-confirm">
            Add to {selected.length > 0 ? `(${selected.length})` : ""}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};


// ─── Autocomplete Dropdown for Line Item ────────────────────────
export const ItemAutocomplete = ({ value, onChange, onSelect, isDark, headers, endpoint, placeholder, inputClassName, testId }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowSuggestions(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const loadItems = async () => {
    if (loaded) return;
    try {
      const res = await axios.get(endpoint || `${API}/invoices/library/items`, { headers });
      setAllItems(res.data || []);
      setLoaded(true);
    } catch { /* silent */ }
  };

  const handleChange = (val) => {
    onChange(val);
    if (val.length >= 1 && allItems.length > 0) {
      const matches = allItems.filter(i =>
        i.name.toLowerCase().includes(val.toLowerCase()) ||
        (i.sku || "").toLowerCase().includes(val.toLowerCase())
      ).slice(0, 6);
      setSuggestions(matches);
      setShowSuggestions(matches.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        value={value}
        onChange={e => handleChange(e.target.value)}
        onFocus={() => { loadItems(); if (value && suggestions.length > 0) setShowSuggestions(true); }}
        className={inputClassName || "h-8 text-sm"}
        placeholder={placeholder || "Type to search saved items..."}
        data-testid={testId}
      />
      {showSuggestions && (
        <div className={`absolute top-full left-0 right-0 mt-1 rounded-lg border shadow-lg z-50 max-h-48 overflow-y-auto overscroll-contain touch-pan-y ${isDark ? "bg-[#1a1a1f] border-white/10" : "bg-white border-gray-200"}`}>
          {suggestions.map(item => (
            <button key={item.item_id} type="button" data-testid={`saved-item-suggestion-${item.item_id}`}
              onClick={() => { onSelect(item); setShowSuggestions(false); }}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between transition-colors ${isDark ? "hover:bg-white/5" : "hover:bg-gray-50"}`}
            >
              <div className="min-w-0">
                <span className="font-medium text-foreground">{item.name}</span>
                {item.sku && <span className="text-[10px] text-muted-foreground ml-1.5">({item.sku})</span>}
              </div>
              <span className={`text-xs font-medium shrink-0 ml-2 ${isDark ? "text-green-400" : "text-green-600"}`}>${(item.unit_price || 0).toFixed(2)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


// ─── Quick Save Button for line items ───────────────────────────
export const QuickSaveItemBtn = ({ item, isDark, headers, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!item.description?.trim()) { toast.error("Add a description first"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/invoices/library/items`, {
        name: item.description,
        unit_price: item.unit_price || 0,
        default_quantity: item.quantity || 1,
      }, { headers });
      toast.success("Item saved to library");
      onSaved?.();
    } catch { toast.error("Failed to save item"); }
    finally { setSaving(false); }
  };
  return (
    <Button type="button" variant="ghost" size="icon" onClick={save} disabled={saving} className="h-6 w-6 text-muted-foreground hover:text-[#FFCC00]" title="Save to library" data-testid="quick-save-item">
      <BookmarkPlus className="w-3 h-3" />
    </Button>
  );
};
